// src/App.tsx
import { lazy, Suspense, memo, useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "react-hot-toast";
import { HelmetProvider } from "react-helmet-async";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { LoadingScreen } from "./components/common/LoadingScreen";
import { queryClientConfig } from "./lib/queryClient";
import { ProtectedRoute } from "./routes/ProtectedRoute";
import { APP_CONFIG } from "./config/app.config";
import { useTheme } from "./hooks/useTheme";
import { useAppStore } from "./store/app.store";
import { useLocation } from "react-router-dom";
import { tiktokPixelService } from "./services/tiktok-pixel.service";
import { CampaignProvider } from "./campaigns/providers/CampaignProvider";
import { CampaignMeta } from "./campaigns/components/CampaignMeta";
import { PrivacyConsentProvider } from "./contexts/PrivacyConsentContext";
import { CookieConsentBanner } from "./components/legal/CookieConsentBanner";
// PAGES
import CreateCard from "./pages/CreateCard";
import PaymentPage from "./pages/PaymentPage";
import Dashboard from "./pages/Dashboard";
import SharePage from "./pages/SharePage";
import SignIn from "./pages/SignIn";
import ArchetypeSelect from "./pages/ArtchetypeSelect";
import LandingPage from "./pages/LandingPage";
import ArchetypePreview from "./pages/ArchetypePreview";
import RecipientPage from "./pages/RecipientPage";
import Examples from "./pages/Examples";
import DetailView from "./pages/DetailView";
import TemplatePreview from "./pages/TemplatePreview";
import PreviewPage from "./pages/PreviewPage";
import AmbassadorsPage from "./pages/AmbassadorsPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import PrivacySettings from "./pages/PrivacySettings";

// Lazy load pages for better code splitting
const Layout = lazy(() =>
  import("./components/common/Layout").then((m) => ({ default: m.Layout }))
);

const queryClient = new QueryClient(queryClientConfig);

const routeConfig = [
  { path: "/", element: <LandingPage />, public: true },
  { path: "/signin", element: <SignIn />, public: true },
  { path: "/archetypes", element: <ArchetypeSelect />, public: true },
  {
    path: "/preview/archetype/:archetypeId",
    element: <ArchetypePreview />,
    public: true,
  },
  { path: "/recipient/:cardId", element: <RecipientPage />, public: true },
  { path: "/examples", element: <Examples />, public: true },
  { path: "/detail/:archetypeId", element: <DetailView />, public: true },
  { path: "/create", element: <CreateCard />, public: true },
  { path: "/create/:cardId", element: <CreateCard />, public: true },
  {
    path: "/preview/template/:templateId",
    element: <TemplatePreview />,
    public: true,
  },
  { path: "/preview/:cardId", element: <PreviewPage />, protected: true },
  { path: "/payment/:cardId", element: <PaymentPage />, protected: true },
  { path: "/share/:cardId", element: <SharePage />, protected: true },
  { path: "/dashboard", element: <Dashboard />, protected: true },
  { path: "*", element: <LandingPage />, public: true },
  { path: "/ambassadors", element: <AmbassadorsPage />, public: true },
  { path: "/privacy", element: <PrivacyPolicy />, public: true },
  { path: "/terms", element: <TermsOfService />, public: true },
  { path: "/settings/privacy", element: <PrivacySettings />, public: true },
];

const ToasterWithTheme = memo(() => {
  const theme = useAppStore((state) => state.theme);
  return (
    <Toaster
      position="top-center"
      toastOptions={{
        duration: 4000,
        style: {
          background: theme === "light" ? "#ffffff" : "#2a2725",
          color: theme === "light" ? "#3e3a3b" : "#f0e7e0",
          border: `1px solid ${theme === "light" ? "#e8ddd7" : "#3e3935"}`,
          boxShadow:
            theme === "light"
              ? "0 8px 32px rgba(62, 58, 59, 0.08)"
              : "0 8px 32px rgba(0, 0, 0, 0.5)",
          borderRadius: "16px",
          backdropFilter: "blur(8px)",
        },
        success: {
          icon: "",
          style: {
            background: "#000000", // Black background
            color: "#ffffff", // White text
            border: "1px solid #333333", // Optional: dark border
          },
        },
        error: {
          icon: "❌",
          style: {
            background: "#000000", // Black background
            color: "#ffffff", // White text
            border: "1px solid #333333", // Optional: dark border
          },
        },
      }}
    />
  );
});

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        {routeConfig.map(({ path, element, protected: isProtected }) => (
          <Route
            key={path}
            path={path}
            element={isProtected ? <ProtectedRoute>{element}</ProtectedRoute> : element}
          />
        ))}
      </Route>
    </Routes>
  );
}

function AppContent() {
  useTheme();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), APP_CONFIG.loading.minDisplayTime);
    return () => clearTimeout(timer);
  }, []);

  if (!isReady) return <LoadingScreen />;

  function TikTokPixelBootstrap() {
    const location = useLocation();

    useEffect(() => {
      tiktokPixelService.init();

      const rerun = () => {
        tiktokPixelService.init();
        tiktokPixelService.page();
      };

      window.addEventListener("lrnsy:privacy-consent-updated", rerun);
      return () => window.removeEventListener("lrnsy:privacy-consent-updated", rerun);
    }, []);

    useEffect(() => {
      tiktokPixelService.page();
    }, [location.pathname, location.search]);

    return null;
  }

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <PrivacyConsentProvider>
          <CampaignProvider>
            <CampaignMeta />
            <QueryClientProvider client={queryClient}>
              <Router>
                <TikTokPixelBootstrap />
                <Suspense fallback={<LoadingScreen />}>
                  <AppRoutes />
                </Suspense>
                <CookieConsentBanner />
              </Router>
              <ToasterWithTheme />
              {process.env.NODE_ENV === "development" && <ReactQueryDevtools />}
            </QueryClientProvider>
          </CampaignProvider>
        </PrivacyConsentProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default memo(AppContent);
